import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { Gauge } from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import type { FreshnessSLO } from '@vehix/shared/types/recallSnapshots';

interface Props {
  slo: FreshnessSLO;
  /**
   * The OLDEST tenant-scoped snapshot date in view — the worst case, not the best.
   *
   * Snapshot dates are per-tenant cutoffs and can sit weeks apart, so reporting the newest
   * one beside a "within SLO" pill let a single current tenant vouch for the whole fleet.
   * This card answers "can I trust these numbers", so it reports the laggard.
   */
  oldestSnapshotDate: string | null;
}

function formatHoursAgo(hours: number | null): string {
  if (hours == null) return '—';
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${hours}h ago`;
  const d = Math.floor(hours / 24);
  return `${d}d ${hours % 24}h ago`;
}

export const FreshnessContractCard: React.FC<Props> = ({ slo, oldestSnapshotDate }) => {
  const pct = Math.min(100, Math.max(0, slo.pct_of_slo));
  const barClass = !slo.within_slo ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <Card className='p-4'>
      <div className='flex items-start gap-3'>
        <Gauge className='h-5 w-5 text-primary flex-shrink-0 mt-0.5' />
        <div className='min-w-0 flex-1 space-y-2'>
          <div className='flex items-baseline justify-between gap-2'>
            <h3 className='text-sm font-semibold'>Freshness contract</h3>
            <span
              className={cn(
                'text-[11px] font-medium px-1.5 py-0.5 rounded',
                slo.within_slo
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300'
              )}
            >
              {slo.within_slo ? 'within SLO' : 'breached'}
            </span>
          </div>
          <div className='space-y-1'>
            <div className='flex justify-between text-xs text-muted-foreground'>
              <span>Last refresh: {formatHoursAgo(slo.hours_since_refresh)}</span>
              <span>{pct}% of SLO</span>
            </div>
            <div className='relative h-1.5 w-full overflow-hidden rounded-full bg-secondary'>
              <div
                className={cn('h-full transition-all', barClass)}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className='flex justify-between text-[11px] text-muted-foreground pt-0.5'>
              <span>SLO: refresh ≤ {slo.slo_hours}h</span>
              {oldestSnapshotDate && (
                <span title='The oldest tenant-scoped data cutoff in view. Recall counts are counted as of a per-tenant crawl date, so this is the laggard, not the fleet average. It is a different measure from "last refresh", which reports when the refresh job last ran.'>
                  Counts as old as: {oldestSnapshotDate}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default FreshnessContractCard;
